import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {useParams} from 'react-router-dom/cjs/react-router-dom.min'
import {HTTP_STATUS_OK, SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {useAuth} from '../../authentication/AuthProvider'
import {getClientId} from '../../authentication/AuthUtils'
import {actionTableKeys} from '../../new-table/Constants'
import {permissionOptionKeys} from '../../role/RoleConstant'
import {useRoleContext} from '../../role/RoleProvider'
import {useImportNotificationContext} from '../../table-utils/importForm/ImportNotificationProvider'
import {
    usePageUserManagements,
    useUserManagementCRUD,
} from '../../user-property/query-keys/UserPropertyQueryManager'
import {UserPropertyType} from '../../user-property/UserPropertyConstants'
import {getPageUserManagements} from '../../user-property/UserPropertyEndPoint'
import {
    filterDataTableEvent,
    filterEventTypes,
    getFilterEventKey,
    removeFilterChangeListener,
} from '../broadcast/FilterEvent'
import {
    exportColumnTypes,
    exportRecordTypes,
    exportTypes,
    tableToolbarType,
} from '../Constant'
import {DatatableContext} from '../DataTableContext'
import StyledDataTableWrapper from '../StyledDataTableWrapper'
import {
    getAdvancedFilter,
    getShouldRememberAdvancedFilter,
    tableTypes,
} from '../StyledTableUtils'
import {
    getHealthCareEventKey,
    healthCareEvent,
    healthCareEventTypes,
} from './broadcast/HealthCarePractitionerEvent'
import {hcpPatientColumnList} from './HealthCarePractitionerConstants'

const StyledHealthCarePractitionerManagementDatatable = () => {
    useSignals()

    const queryClient = useQueryClient()
    //#region ID
    const {id: idString} = useParams()
    const id = useMemo(() => {
        if (!idString) {
            return null
        }
        return parseInt(idString)
    }, [idString])
    //#endregion

    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {hcpManagementPermissions} = useRoleContext()
    const {handleOnExportDatatable} = useImportNotificationContext()
    //#endregion

    //#region Load Data from Local Storage
    const initialAdvancedFilterRequest = useMemo(() => {
        const didRememberSettings = getShouldRememberAdvancedFilter(
            tableTypes.hcpPatient,
        )

        if (didRememberSettings) {
            const storedFilterRequest = getAdvancedFilter(tableTypes.hcpPatient)
            if (
                !!storedFilterRequest &&
                Object.keys(storedFilterRequest).length > 0
            ) {
                return storedFilterRequest
            }
        }

        return {}
    }, [])
    //#endregion

    //#region State Controls
    const pageable = useSignal({
        pageRows: 10,
        pageNumber: 0,
        sortField: 'createdAt',
        sortOrder: -1,
    })

    const filterRequest = useSignal({
        globalSearchValue: null,
        mainRefId: id,
        hospitalId:
            userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        ...initialAdvancedFilterRequest,
    })

    // Pageable
    const handleChangeSortColumn = useCallback(
        e => {
            pageable.value = {
                ...pageable.value,
                sortField: e.sortField,
                sortOrder: e.sortOrder === 1 ? -1 : 1,
            }
        },
        [pageable.value.sortField, pageable.value.sortOrder],
    )

    const handleChangePageNumber = useCallback(
        newPageNumber => {
            pageable.value = {
                ...pageable.value,
                pageNumber: newPageNumber,
            }

            handlePrefetchDataByPage(newPageNumber + 1)
        },
        [pageable.value.pageNumber],
    )

    const handleChangePageRows = useCallback(
        newPageRows => {
            pageable.value = {
                ...pageable.value,
                pageRows: newPageRows,
                pageNumber: 0,
            }

            pagePrefetchRef.current = [0]

            handlePrefetchDataByPage(1)
        },
        [pageable.value.pageRows],
    )

    // Global Search Value
    const handleChangeGlobalSearch = useCallback(value => {
        filterRequest.value = {
            ...filterRequest.value,
            globalSearchValue: value || null,
        }
    }, [])

    // Filter Request
    const handleClearFilterRequest = () => {
        filterRequest.value = {
            globalSearchValue: null,
            mainRefId: id,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
        }
    }

    const handleClearFilterRequestByKey = useCallback((key, value) => {
        filterRequest.value = {
            ...filterRequest.value,
            [key]: value,
        }
    }, [])

    const handleClearAdvancedFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: filterRequest.value?.globalSearchValue || null,
            mainRefId: id,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
        }
    }, [])

    // Table Action CRUD
    const formRef = useRef(null)

    const {mutateAsync: deleteUserManagement} = useUserManagementCRUD(
        QueryCRUDTypes.delete,
        user?.id,
    )

    // const handleAddNew = useCallback(() => {
    //     formRef.current?.showForm()
    // }, [])

    const handleOnDelete = async data => {
        if (!!data && !!data?.id) {
            const response = await deleteUserManagement(data)
            if (response === HTTP_STATUS_OK) {
                const {userPropertyKeys} = QueryKeyManager(
                    QueryKeyTypes.userProperty,
                    user?.id,
                )

                //TODO: Temporary Refetch Table
                queryClient.invalidateQueries(
                    userPropertyKeys.pattern({
                        type: UserPropertyType.userManagement,
                    }),
                )
            }
        }
    }

    const handleAction = (type, rowValue) => {
        if (type === actionTableKeys.delete && rowValue) {
            handleOnDelete(rowValue)
        }
    }
    //#endregion

    //#region Check Permission
    const handleCheckPermissionByKey = useCallback(
        key => {
            if (!key) {
                return false
            }

            if (userRole === SystemRole.ADMIN) {
                return true
            }

            if (!hcpManagementPermissions?.length) {
                return false
            }

            if (key === permissionOptionKeys.allowToDelete) {
                return hcpManagementPermissions?.includes(
                    permissionOptionKeys.allowToRemoveHcpPatient,
                )
            } else {
                return hcpManagementPermissions?.includes(key)
            }
        },
        [userRole, hcpManagementPermissions],
    )
    //#endregion

    //#region Data Controls
    const getCurrentQueryKey = ({newPageNumber, newPageRows}) => {
        const {userPropertyKeys} = QueryKeyManager(
            QueryKeyTypes.userProperty,
            user?.id,
        )
        return userPropertyKeys.page({
            type: UserPropertyType.userManagement,
            pageNumber:
                newPageNumber !== null && typeof newPageNumber === 'number'
                    ? newPageNumber
                    : pageable.value.pageNumber,
            pageRows:
                newPageRows !== null && typeof newPageNumber === 'number'
                    ? newPageRows
                    : pageable.value.pageRows,
            sortField: pageable.value.sortField,
            sortOrder: pageable.value.sortOrder,
            filterRequest: {
                ...filterRequest.value,
            },
        })
    }

    const {data: queryData, isFetched} = usePageUserManagements(
        user?.id,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        filterRequest.value,
        true,
    )

    const dataList = useRef([])
    const currentDataList = useMemo(() => {
        dataList.current = queryData?.content || []

        return queryData?.content || []
    }, [queryData?.content])

    // Page Rows from Pagination
    const rowsOptionHoveredRef = useRef([10])
    const handlePrefetchDataByRowsOption = useCallback(rows => {
        if (!!user?.id && !rowsOptionHoveredRef.current?.includes(rows)) {
            rowsOptionHoveredRef.current.push(rows)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: 0,
                    newPageRows: rows,
                }),
                queryFn: () =>
                    getPageUserManagements(
                        user?.id,
                        0,
                        rows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    const handleOnDetailRowClick = id => {
        if (!!id) {
            const selectedRowData = dataList.current?.find(row => row.id === id)
            if (!!selectedRowData) {
                formRef.current?.showForm(selectedRowData)
            }
        }
    }

    // Page Number from Pagination
    const pagePrefetchRef = useRef([0])
    const handlePrefetchDataByPage = useCallback(pageNumber => {
        if (!!user?.id && !pagePrefetchRef.current?.includes(pageNumber)) {
            pagePrefetchRef.current.push(pageNumber)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: pageNumber,
                    newPageRows: pageable.peek().pageRows,
                }),
                queryFn: () =>
                    getPageUserManagements(
                        user?.id,
                        pageNumber,
                        pageable.peek().pageRows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    //#region Event Listener on State Changes
    useEffect(() => {
        // HCP'S MANAGEMENT CHANGE
        healthCareEvent.on(
            getHealthCareEventKey(healthCareEventTypes.USER_MANAGEMENT_CHANGE),
            data => {
                if (
                    !data ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                const {userPropertyKeys} = QueryKeyManager(
                    QueryKeyTypes.userProperty,
                    user?.id,
                )

                queryClient.invalidateQueries(
                    userPropertyKeys.pattern({
                        type: UserPropertyType.userManagement,
                    }),
                )
            },
        )

        // PREFECT
        handlePrefetchDataByPage(pageable.value.pageNumber + 1)

        // FILTER
        filterDataTableEvent.on(
            `${getFilterEventKey(
                tableTypes.hcpPatient,
                filterEventTypes.advanced,
            )}`,
            newFilterConfig => {
                if (!newFilterConfig || typeof newFilterConfig !== 'object') {
                    return
                }

                if (Object.keys(newFilterConfig).length === 0) {
                    handleClearAdvancedFilterRequest()

                    return
                }

                filterRequest.value = {
                    ...filterRequest.value,
                    ...newFilterConfig,
                }
            },
        )

        return () => {
            removeFilterChangeListener(
                tableTypes.hcpPatient,
                filterEventTypes.advanced,
            )
        }
    }, [])
    //#endregion

    //#region Export Data
    const handleRetrieveExportData = async (
        {
            exportType = exportTypes.excel, // csv, excel, pdf
            exportRecordType = exportRecordTypes.allRecords, // all, currentInView
            exportColumns = exportColumnTypes.allColumns, // all, currentInView
        },
        columns,
    ) => {
        handleOnExportDatatable(
            {
                exportType: exportType,
                exportRecordType: exportRecordType,
                exportColumns: exportColumns,
            },
            queryData,
            pageable.value,
            filterRequest.value,
            columns,
            tableTypes.hcpPatient,
        )
    }
    //#endregion

    return (
        <DatatableContext.Provider
            value={{
                dataKey: 'id',
                tableType: tableTypes.hcpPatient,
                queryData: queryData,
                isFetched: isFetched,
                columnList: hcpPatientColumnList,
                tableToolbar: [tableToolbarType.tableControlToolbar],
                tableActions: [actionTableKeys.delete],

                pageable: pageable.value,
                onChangeSortColumn: handleChangeSortColumn,
                onChangePageNumber: handleChangePageNumber,
                onChangePageRows: handleChangePageRows,
                onPageRowsHovered: handlePrefetchDataByRowsOption,

                onDetailRowClick: handleOnDetailRowClick,
                handleCheckPermissionByKey: handleCheckPermissionByKey,

                initialAdvancedFilterRequest: initialAdvancedFilterRequest,
                handleClearFilterRequest: handleClearFilterRequest,
                handleClearFilterRequestByKey: handleClearFilterRequestByKey,
                handleClearAdvancedFilterRequest:
                    handleClearAdvancedFilterRequest,

                handleRetrieveExportData: handleRetrieveExportData,

                handleChangeGlobalSearch: handleChangeGlobalSearch,

                hideCreateButton: true,
                // handleAddNew: handleAddNew,
                handleAction: handleAction,
            }}>
            <StyledDataTableWrapper
                headerOptions={{
                    title: "HCP's Patients Management",
                    subTitle:
                        'Managing patients currently assigned for treatment and care under your responsibility.',
                }}
            />
        </DatatableContext.Provider>
    )
}

export default StyledHealthCarePractitionerManagementDatatable
